/**
 * Employee Detail Panel Component
 *
 * Shows comprehensive consent, referral, and workflow status for an employee.
 * Allows staff to:
 * - View existing consents and referrals
 * - Accept consent on behalf of an employee
 * - Create manual referrals
 * - Track workflow progress
 */

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  User,
  Building2,
  Briefcase,
  Mail,
  Phone,
  CheckCircle,
  Clock,
  AlertTriangle,
  FileText,
  Activity,
  Plus,
  ExternalLink,
  Shield,
  RefreshCw,
  Loader2,
  ChevronRight,
  X,
  Play,
  Heart,
  Home,
  Utensils,
  Car,
  DollarSign,
  GraduationCap,
  Users,
  HeartPulse,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  serviceReferralsAPI,
  EmployeeConsentReferralStatus,
  EmployeeConsentRecord,
  EmployeeReferralRecord,
  AvailableProgram,
  WorkflowTemplate,
} from '@/services/api/serviceReferrals';
import { PricingContextPanel } from '@/components/service-referrals/PricingContextPanel';
import { getApiBaseUrl } from '@/lib/utils';
import { authFetch } from '@/lib/authFetch';

interface EmployeeDetailPanelProps {
  employeeId: number | null;
  employeeName?: string;
  isOpen: boolean;
  onClose: () => void;
  onRefresh?: () => void;
}

interface ServiceType {
  code: string;
  name: string;
}

// SDOH Domain definitions
const SDOH_DOMAINS = [
  { code: 'HOUSING', name: 'Housing', icon: Home, description: 'Housing stability, affordability, safety' },
  { code: 'FOOD_NUTRITION', name: 'Food & Nutrition', icon: Utensils, description: 'Food security, nutrition access' },
  { code: 'TRANSPORTATION', name: 'Transportation', icon: Car, description: 'Transportation access for medical/work' },
  { code: 'ECONOMIC_STABILITY', name: 'Economic Stability', icon: DollarSign, description: 'Financial stress, employment stability' },
  { code: 'EDUCATION_EMPLOYMENT', name: 'Education & Employment', icon: GraduationCap, description: 'Education, job training, employment' },
  { code: 'SOCIAL_SUPPORT', name: 'Social Support', icon: Users, description: 'Social isolation, family support' },
  { code: 'HEALTH_BEHAVIORS', name: 'Health Behaviors', icon: HeartPulse, description: 'Tobacco, substance use, physical activity' },
  { code: 'SAFETY', name: 'Safety', icon: Shield, description: 'Personal safety, domestic violence' },
];

const SDOH_SEVERITY_LEVELS = [
  { value: 'low', label: 'Low', color: 'bg-green-100 text-green-700' },
  { value: 'moderate', label: 'Moderate', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700' },
];

interface SDOHNeedInput {
  domain_code: string;
  severity: string;
  description: string;
}

export function EmployeeDetailPanel({
  employeeId,
  employeeName,
  isOpen,
  onClose,
  onRefresh,
}: EmployeeDetailPanelProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<EmployeeConsentReferralStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false);

  // Form states
  const [selectedProgram, setSelectedProgram] = useState<number | null>(null);
  const [consentNotes, setConsentNotes] = useState('');
  const [createReferralWithConsent, setCreateReferralWithConsent] = useState(true);
  const [selectedServiceType, setSelectedServiceType] = useState<string>('');
  const [referralUrgency, setReferralUrgency] = useState<'routine' | 'urgent' | 'emergency'>('routine');
  const [referralDescription, setReferralDescription] = useState('');
  const [triggerMatching, setTriggerMatching] = useState(true);
  const [selectedWorkflowTemplate, setSelectedWorkflowTemplate] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // SDOH Assessment states
  const [selectedSDOHDomains, setSelectedSDOHDomains] = useState<string[]>([]);
  const [primarySDOHDomain, setPrimarySDOHDomain] = useState<string>('');
  const [sdohSeverity, setSDOHSeverity] = useState<string>('moderate');
  const [sdohNeeds, setSDOHNeeds] = useState<SDOHNeedInput[]>([]);
  const [sdohNotes, setSDOHNotes] = useState('');
  const [referralDialogTab, setReferralDialogTab] = useState<'service' | 'sdoh' | 'pricing'>('service');

  // Service types for manual referral
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);

  // Load employee status
  const loadEmployeeStatus = useCallback(async () => {
    if (!employeeId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await serviceReferralsAPI.getEmployeeConsentReferralStatus(employeeId);
      setStatus(data);

      // Load service types for manual referral
      // Check both auth_token (admin/CBO portal) and member_token (member/employer portal)
      const response = await authFetch(`${getApiBaseUrl()}/api/service-taxonomy/services`);
      if (response.ok) {
        const types = await response.json();
        setServiceTypes(types || []);
      }
    } catch (err) {
      console.error('Error loading employee status:', err);
      setError('Failed to load employee details');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (isOpen && employeeId) {
      loadEmployeeStatus();
    }
  }, [isOpen, employeeId, loadEmployeeStatus]);

  // Handle accept consent on behalf
  const handleAcceptConsent = async () => {
    if (!selectedProgram || !employeeId) return;

    setSubmitting(true);
    try {
      const result = await serviceReferralsAPI.acceptConsentOnBehalf({
        employee_id: employeeId,
        program_id: selectedProgram,
        notes: consentNotes || undefined,
        create_referral: createReferralWithConsent,
      });

      toast({
        title: 'Consent Accepted',
        description: result.message,
      });

      setShowConsentDialog(false);
      setSelectedProgram(null);
      setConsentNotes('');
      setCreateReferralWithConsent(true);

      // Reload status
      await loadEmployeeStatus();
      onRefresh?.();
    } catch (err: any) {
      console.error('Error accepting consent:', err);
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to accept consent',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle create manual referral
  const handleCreateReferral = async () => {
    if (!selectedServiceType || !employeeId) return;

    setSubmitting(true);
    try {
      // Build SDOH needs array from selected domains
      const sdohNeedsPayload = selectedSDOHDomains.map(domainCode => ({
        sdoh_domain_code: domainCode,
        severity: domainCode === primarySDOHDomain ? sdohSeverity : 'moderate',
        is_primary: domainCode === primarySDOHDomain,
        need_description: domainCode === primarySDOHDomain ? sdohNotes : undefined,
      }));

      const result = await serviceReferralsAPI.createManualReferral({
        employee_id: employeeId,
        service_type_code: selectedServiceType,
        urgency: referralUrgency,
        service_description: referralDescription || undefined,
        trigger_matching: triggerMatching,
        // SDOH fields
        sdoh_domain_code: primarySDOHDomain || undefined,
        sdoh_risk_level: primarySDOHDomain ? sdohSeverity : undefined,
        sdoh_needs: sdohNeedsPayload.length > 0 ? sdohNeedsPayload : undefined,
        clinical_notes: sdohNotes || undefined,
      });

      toast({
        title: 'Referral Created',
        description: `${result.message}. ${result.matched_providers_count} providers matched.`,
      });

      // Reset all form fields
      setShowReferralDialog(false);
      setSelectedServiceType('');
      setReferralUrgency('routine');
      setReferralDescription('');
      setTriggerMatching(true);
      setSelectedSDOHDomains([]);
      setPrimarySDOHDomain('');
      setSDOHSeverity('moderate');
      setSDOHNotes('');
      setReferralDialogTab('service');

      // Reload status
      await loadEmployeeStatus();
      onRefresh?.();
    } catch (err: any) {
      console.error('Error creating referral:', err);
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to create referral',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle SDOH domain selection
  const toggleSDOHDomain = (domainCode: string) => {
    setSelectedSDOHDomains(prev => {
      if (prev.includes(domainCode)) {
        // Remove domain
        const newDomains = prev.filter(d => d !== domainCode);
        // If we removed the primary, clear it
        if (primarySDOHDomain === domainCode) {
          setPrimarySDOHDomain(newDomains[0] || '');
        }
        return newDomains;
      } else {
        // Add domain
        const newDomains = [...prev, domainCode];
        // If this is the first domain, make it primary
        if (newDomains.length === 1) {
          setPrimarySDOHDomain(domainCode);
        }
        return newDomains;
      }
    });
  };

  // Handle initiate workflow
  const handleInitiateWorkflow = async () => {
    if (!selectedWorkflowTemplate || !employeeId) return;

    setSubmitting(true);
    try {
      const result = await serviceReferralsAPI.initiateWorkflow({
        employee_id: employeeId,
        template_id: selectedWorkflowTemplate,
      });

      toast({
        title: 'Workflow Initiated',
        description: result.message,
      });

      setShowWorkflowDialog(false);
      setSelectedWorkflowTemplate(null);

      // Reload status
      await loadEmployeeStatus();
      onRefresh?.();
    } catch (err: any) {
      console.error('Error initiating workflow:', err);
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to initiate workflow',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Get consent status badge
  const getConsentStatusBadge = (consentStatus: string) => {
    switch (consentStatus) {
      case 'accepted':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="w-3 h-3 mr-1" />
            Accepted
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case 'revoked':
        return (
          <Badge className="bg-red-100 text-red-800 border-red-200">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Revoked
          </Badge>
        );
      default:
        return <Badge variant="outline">{consentStatus}</Badge>;
    }
  };

  // Get referral status badge
  const getReferralStatusBadge = (referralStatus: string) => {
    const statusColors: Record<string, string> = {
      created: 'bg-gray-100 text-gray-800',
      matching: 'bg-blue-100 text-blue-800',
      matched: 'bg-blue-100 text-blue-800',
      provider_accepted: 'bg-green-100 text-green-800',
      provider_declined: 'bg-red-100 text-red-800',
      service_started: 'bg-purple-100 text-purple-800',
      service_completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };

    return (
      <Badge className={statusColors[referralStatus] || 'bg-gray-100 text-gray-800'}>
        {referralStatus.replace(/_/g, ' ')}
      </Badge>
    );
  };

  // Filter programs that don't have accepted consent
  const availableProgramsForConsent = status?.available_programs.filter(
    (p) => !status.consents.find((c) => c.program_id === p.program_id && c.status === 'accepted')
  );

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent className="w-[500px] sm:w-[600px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {employeeName || status?.employee.name || 'Employee Details'}
            </SheetTitle>
            <SheetDescription>
              View and manage consent, referrals, and workflows
            </SheetDescription>
          </SheetHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <Alert variant="destructive" className="mt-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : status ? (
            <div className="mt-6 space-y-6">
              {/* Employee Info Card */}
              <Card>
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{status.employee.email || 'No email'}</span>
                    </div>
                    {status.employee.department && (
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span>{status.employee.department}</span>
                      </div>
                    )}
                    {status.employee.job_title && (
                      <div className="flex items-center gap-2 text-sm">
                        <Briefcase className="h-4 w-4 text-muted-foreground" />
                        <span>{status.employee.job_title}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Building2 className="h-4 w-4" />
                      <span>{status.employee.employer_name}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-3">
                <Card className={status.summary.has_accepted_consent ? 'border-green-200' : ''}>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold">{status.summary.total_consents}</p>
                    <p className="text-xs text-muted-foreground">Consents</p>
                    {status.summary.has_accepted_consent && (
                      <Badge className="mt-1 bg-green-100 text-green-800 text-xs">Active</Badge>
                    )}
                  </CardContent>
                </Card>
                <Card className={status.summary.has_active_referral ? 'border-blue-200' : ''}>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold">{status.summary.total_referrals}</p>
                    <p className="text-xs text-muted-foreground">Referrals</p>
                    {status.summary.has_active_referral && (
                      <Badge className="mt-1 bg-blue-100 text-blue-800 text-xs">Active</Badge>
                    )}
                  </CardContent>
                </Card>
                <Card className={status.summary.has_active_workflow ? 'border-purple-200' : ''}>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold">{status.summary.total_workflows}</p>
                    <p className="text-xs text-muted-foreground">Workflows</p>
                    {status.summary.has_active_workflow && (
                      <Badge className="mt-1 bg-purple-100 text-purple-800 text-xs">Active</Badge>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Staff Actions */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Staff Actions</CardTitle>
                  <CardDescription>Execute actions on behalf of employee</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button
                    className="w-full justify-between"
                    variant="outline"
                    onClick={() => setShowConsentDialog(true)}
                    disabled={!availableProgramsForConsent?.length}
                  >
                    <span className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Accept Consent on Behalf
                    </span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    className="w-full justify-between"
                    variant="outline"
                    onClick={() => setShowReferralDialog(true)}
                    disabled={!serviceTypes.length}
                  >
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Create Manual Referral
                    </span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    className="w-full justify-between"
                    variant="outline"
                    onClick={() => setShowWorkflowDialog(true)}
                    disabled={!status?.workflow_templates?.length || status?.summary.has_active_workflow}
                  >
                    <span className="flex items-center gap-2">
                      <Play className="h-4 w-4" />
                      Start Workflow
                    </span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    className="w-full justify-between"
                    variant="ghost"
                    onClick={loadEmployeeStatus}
                  >
                    <span className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4" />
                      Refresh Status
                    </span>
                  </Button>
                </CardContent>
              </Card>

              <Separator />

              {/* Consents Section */}
              <div>
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Program Consents
                </h3>
                {status.consents.length === 0 ? (
                  <div className="text-center py-4 text-sm text-muted-foreground bg-muted rounded-lg">
                    No consent records
                  </div>
                ) : (
                  <div className="space-y-2">
                    {status.consents.map((consent) => (
                      <Card key={consent.consent_id} className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{consent.program_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {consent.accepted_at
                                ? `Accepted ${fmt.short(consent.accepted_at)}`
                                : consent.offered_at
                                  ? `Offered ${fmt.short(consent.offered_at)}`
                                  : 'No date'}
                            </p>
                          </div>
                          {getConsentStatusBadge(consent.status)}
                        </div>
                        {consent.signature_method && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Method: {consent.signature_method}
                          </p>
                        )}
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Referrals Section */}
              <div>
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Service Referrals
                </h3>
                {status.referrals.length === 0 ? (
                  <div className="text-center py-4 text-sm text-muted-foreground bg-muted rounded-lg">
                    No referrals
                  </div>
                ) : (
                  <div className="space-y-2">
                    {status.referrals.map((referral) => (
                      <Card key={referral.referral_id} className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">
                              {referral.service_type_name || referral.service_type_code}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {referral.referral_number} •{' '}
                              {referral.created_at
                                ? fmt.short(referral.created_at)
                                : 'No date'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {getReferralStatusBadge(referral.status)}
                            <Badge variant="outline" className="text-xs">
                              {referral.urgency}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Source: {referral.source_type}
                        </p>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Workflows Section */}
              <div>
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Workflow History
                </h3>
                {status.workflows.length === 0 ? (
                  <div className="text-center py-4 text-sm text-muted-foreground bg-muted rounded-lg">
                    No workflows
                  </div>
                ) : (
                  <div className="space-y-2">
                    {status.workflows.map((workflow) => (
                      <Card key={workflow.workflow_id} className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{workflow.template_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {workflow.initiated_at
                                ? `Started ${fmt.short(workflow.initiated_at)}`
                                : 'Not started'}
                            </p>
                          </div>
                          <Badge
                            variant={
                              workflow.status === 'completed'
                                ? 'default'
                                : workflow.status === 'in_progress'
                                  ? 'secondary'
                                  : 'outline'
                            }
                          >
                            {workflow.status}
                          </Badge>
                        </div>
                        {workflow.referral_created && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-green-600">
                            <CheckCircle className="h-3 w-3" />
                            Referral created
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Accept Consent Dialog */}
      <Dialog open={showConsentDialog} onOpenChange={setShowConsentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept Consent on Behalf of Employee</DialogTitle>
            <DialogDescription>
              Use this when an employee has verbally consented or needs assistance.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Program</Label>
              <Select
                value={selectedProgram?.toString() || ''}
                onValueChange={(v) => setSelectedProgram(parseInt(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a program..." />
                </SelectTrigger>
                <SelectContent>
                  {availableProgramsForConsent?.map((program) => (
                    <SelectItem key={program.program_id} value={program.program_id.toString()}>
                      {program.program_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Document how consent was obtained..."
                value={consentNotes}
                onChange={(e) => setConsentNotes(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="create-referral">Also create service referral</Label>
              <Switch
                id="create-referral"
                checked={createReferralWithConsent}
                onCheckedChange={setCreateReferralWithConsent}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConsentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAcceptConsent} disabled={!selectedProgram || submitting}>
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Accept Consent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Referral Dialog - Enhanced with SDOH Assessment */}
      <Dialog open={showReferralDialog} onOpenChange={setShowReferralDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Create Service Referral
            </DialogTitle>
            <DialogDescription>
              Create a service referral with SDOH assessment. Complete both sections for comprehensive care coordination.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={referralDialogTab} onValueChange={(v) => setReferralDialogTab(v as 'service' | 'sdoh' | 'pricing')}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="service" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Service
              </TabsTrigger>
              <TabsTrigger value="pricing" className="flex items-center gap-2" disabled={!selectedServiceType}>
                <DollarSign className="h-4 w-4" />
                Pricing
              </TabsTrigger>
              <TabsTrigger value="sdoh" className="flex items-center gap-2">
                <Heart className="h-4 w-4" />
                SDOH
                {selectedSDOHDomains.length > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                    {selectedSDOHDomains.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Service Details Tab */}
            <TabsContent value="service" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Service Type *</Label>
                <Select value={selectedServiceType} onValueChange={setSelectedServiceType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select service type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map((st) => (
                      <SelectItem key={st.code} value={st.code}>
                        {st.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Urgency</Label>
                  <Select value={referralUrgency} onValueChange={(v: any) => setReferralUrgency(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="routine">Routine</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="emergency">Emergency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end pb-2">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="trigger-matching"
                      checked={triggerMatching}
                      onCheckedChange={setTriggerMatching}
                    />
                    <Label htmlFor="trigger-matching" className="text-sm">Auto-match providers</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Referral Description</Label>
                <Textarea
                  placeholder="Describe the referral reason and any specific needs..."
                  value={referralDescription}
                  onChange={(e) => setReferralDescription(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Quick SDOH Summary if domains selected */}
              {selectedSDOHDomains.length > 0 && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">SDOH Needs Identified</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {selectedSDOHDomains.map(code => {
                      const domain = SDOH_DOMAINS.find(d => d.code === code);
                      const isPrimary = code === primarySDOHDomain;
                      return (
                        <Badge
                          key={code}
                          variant={isPrimary ? 'default' : 'outline'}
                          className={isPrimary ? 'bg-blue-600' : ''}
                        >
                          {domain?.name}
                          {isPrimary && ' (Primary)'}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Pricing Context Tab */}
            <TabsContent value="pricing" className="space-y-4 mt-4">
              {selectedServiceType ? (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600">
                    Review the pricing information for <span className="font-medium">{serviceTypes.find(st => st.code === selectedServiceType)?.name || selectedServiceType}</span>
                  </div>
                  <PricingContextPanel
                    serviceTypeCode={selectedServiceType}
                    urgencyLevel={referralUrgency === 'emergency' ? 'emergent' : referralUrgency}
                    showComparatives={true}
                    showMLRecommendation={true}
                    compact={false}
                  />
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <DollarSign className="h-4 w-4 text-blue-600 mt-0.5" />
                      <div className="text-sm text-blue-900">
                        <p className="font-medium">Pricing is set automatically</p>
                        <p className="text-blue-700 mt-1">
                          The pricing shown above will be used as the initial proposal when matching with providers.
                          Providers can accept, counter, or negotiate the rate during the acceptance process.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <DollarSign className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>Select a service type first to see pricing information</p>
                </div>
              )}
            </TabsContent>

            {/* SDOH Assessment Tab */}
            <TabsContent value="sdoh" className="space-y-4 mt-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Select SDOH Domains</Label>
                  <span className="text-xs text-muted-foreground">
                    {selectedSDOHDomains.length} selected
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Identify social determinants affecting this employee. Select all that apply.
                </p>

                <div className="grid grid-cols-2 gap-2">
                  {SDOH_DOMAINS.map((domain) => {
                    const Icon = domain.icon;
                    const isSelected = selectedSDOHDomains.includes(domain.code);
                    const isPrimary = primarySDOHDomain === domain.code;

                    return (
                      <div
                        key={domain.code}
                        className={`
                          relative p-3 rounded-lg border-2 cursor-pointer transition-all
                          ${isSelected
                            ? isPrimary
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-blue-300 bg-blue-50/50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }
                        `}
                        onClick={() => toggleSDOHDomain(domain.code)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}`}>
                            <Icon className={`h-4 w-4 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{domain.name}</span>
                              {isPrimary && (
                                <Badge className="bg-blue-600 text-xs">Primary</Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                              {domain.description}
                            </p>
                          </div>
                          <Checkbox
                            checked={isSelected}
                            className="mt-1"
                            onClick={(e) => e.stopPropagation()}
                            onCheckedChange={() => toggleSDOHDomain(domain.code)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Primary Domain and Severity Selection */}
              {selectedSDOHDomains.length > 0 && (
                <>
                  <Separator />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Primary SDOH Domain</Label>
                      <Select value={primarySDOHDomain} onValueChange={setPrimarySDOHDomain}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select primary..." />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedSDOHDomains.map(code => {
                            const domain = SDOH_DOMAINS.find(d => d.code === code);
                            return (
                              <SelectItem key={code} value={code}>
                                {domain?.name}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Severity Level</Label>
                      <Select value={sdohSeverity} onValueChange={setSDOHSeverity}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SDOH_SEVERITY_LEVELS.map(level => (
                            <SelectItem key={level.value} value={level.value}>
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${level.color.split(' ')[0]}`} />
                                {level.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Clinical Notes / SDOH Details</Label>
                    <Textarea
                      placeholder="Document specific SDOH concerns, observations, and recommended interventions..."
                      value={sdohNotes}
                      onChange={(e) => setSDOHNotes(e.target.value)}
                      rows={4}
                    />
                  </div>
                </>
              )}

              {selectedSDOHDomains.length === 0 && (
                <Alert>
                  <Heart className="h-4 w-4" />
                  <AlertDescription>
                    Select at least one SDOH domain to document social determinants affecting this employee.
                    This information helps match appropriate services and providers.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="flex items-center justify-between border-t pt-4 mt-4">
            <div className="text-sm text-muted-foreground">
              {!selectedServiceType && <span className="text-amber-600">* Service type required</span>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowReferralDialog(false)}>
                Cancel
              </Button>
              {referralDialogTab === 'service' && selectedSDOHDomains.length === 0 && (
                <Button variant="outline" onClick={() => setReferralDialogTab('sdoh')}>
                  Add SDOH Assessment
                </Button>
              )}
              <Button onClick={handleCreateReferral} disabled={!selectedServiceType || submitting}>
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Create Referral
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Initiate Workflow Dialog */}
      <Dialog open={showWorkflowDialog} onOpenChange={setShowWorkflowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start Workflow</DialogTitle>
            <DialogDescription>
              Initiate a consent and referral workflow for this employee.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Workflow Template</Label>
              <Select
                value={selectedWorkflowTemplate?.toString() || ''}
                onValueChange={(v) => setSelectedWorkflowTemplate(parseInt(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a workflow template..." />
                </SelectTrigger>
                <SelectContent>
                  {status?.workflow_templates?.map((template) => (
                    <SelectItem key={template.template_id} value={template.template_id.toString()}>
                      {template.template_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedWorkflowTemplate && (
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium">
                  {status?.workflow_templates?.find((t) => t.template_id === selectedWorkflowTemplate)?.template_name}
                </p>
                <p className="text-muted-foreground mt-1">
                  {status?.workflow_templates?.find((t) => t.template_id === selectedWorkflowTemplate)?.description ||
                    'No description available'}
                </p>
              </div>
            )}
            {status?.summary.has_active_workflow && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  This employee already has an active workflow. Please complete or cancel it before starting a new one.
                </AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWorkflowDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleInitiateWorkflow}
              disabled={!selectedWorkflowTemplate || submitting || status?.summary.has_active_workflow}
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              Start Workflow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default EmployeeDetailPanel;
